import { useEffect, useState } from "react";
import api from "../../services/api";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { faSquarePlus, faSquareMinus, faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FieldDefinition from "../dynamicsFields/FieldDefinition.js";
import { TIPOS_DATO } from "../dynamicsFields/fieldTypes.js";
import EditModal from "../ui/EditModal.js";

const AddEstudiosXPatologia = () => {
  const [IdPatologia, setIdPatologia] = useState("");
  const [IdEstudio, setIdEstudio] = useState("");
  const [allPatologias, setAllPatologias] = useState([]);
  const [allEstudios, setAllEstudios] = useState([]);
  const [estudiosXPatologia, setEstudiosXPatologia] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("activas");
  const [nombreEstudio, setNombreEstudio] = useState("");
  const [fieldDefinition, setFieldDefinition] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [estudioAEditar, setEstudioAEditar] = useState(null);

  useEffect(() => {
    getPatologias();
    getAllEstudios();
  }, []);

  useEffect(() => {
    if (IdPatologia) getEstudiosPorPatologia();
  }, [IdPatologia]);

  const getPatologias = async () => {
    try {
      const response = await api.get(`/patologias`);
      const activas = response.data.filter(p => p.estado === 1);
      setAllPatologias(activas);
    } catch (error) {
      console.error("Error al obtener patologías:", error.message);
    }
  };

  const getAllEstudios = async () => {
    try {
      const response = await api.get(`/getEstudios`);
      const activos = response.data.filter(e => e.estado === 1); 
      setAllEstudios(activos);
    } catch (error) {
      console.error("Error al obtener estudios:", error.message);
    }
  };

  const crearRelacionEstudio = async (e) => {
    e.preventDefault();

    if (!IdPatologia || !IdEstudio) {
      return Swal.fire("Falta completar", "Debe seleccionar una patología y un estudio.", "warning");
    }

    // Validar que el estudio esté activo
    const estudioSeleccionado = allEstudios.find(e => e.IdEstudio === parseInt(IdEstudio));
    if (!estudioSeleccionado || estudioSeleccionado.estado !== 1) {
      return Swal.fire({
        icon: "warning",
        title: "Estudio desactivado",
        text: "No se puede asociar un estudio que está desactivado.",
      });
    }

    // Validar que no exista duplicado (sin importar estado)
    const yaExiste = estudiosXPatologia.some(
      ep => ep.IdEstudio === parseInt(IdEstudio) && ep.IdPatologia === parseInt(IdPatologia)
    );

    if (yaExiste) {
      return Swal.fire({
        icon: "warning",
        title: "Relación duplicada",
        text: "Este estudio ya está asociado a la patología seleccionada.",
      });
    }

    console.log('🔍 fieldDefinition antes de validar:', fieldDefinition);

    // Validar configuración del campo si se especificó algún tipo
    if (fieldDefinition.tipo) {
      // Si es de tipo selección, validar que tenga opciones
      if (
        [TIPOS_DATO.SELECCION, TIPOS_DATO.MULTIPLE].includes(fieldDefinition.tipo) &&
        (!fieldDefinition.configuracion?.opciones || fieldDefinition.configuracion.opciones.length === 0)
      ) {
        return Swal.fire({
          icon: "warning",
          title: "Configuración incompleta",
          text: "Para tipos de selección, debe agregar al menos una opción.",
        });
      }
    }

    try {
      const relacionData = {
        IdPatologia: parseInt(IdPatologia),
        IdEstudio: parseInt(IdEstudio),
      };

      // SIEMPRE enviar json_definicion, si no hay tipo usar "texto" por defecto
      if (fieldDefinition.tipo) {
        relacionData.json_definicion = {
          tipo: fieldDefinition.tipo,
          configuracion: fieldDefinition.configuracion || {}
        };
      } else {
        // Si no se especificó tipo, usar texto por defecto
        relacionData.json_definicion = {
          tipo: "texto",
          configuracion: {}
        };
      }

      console.log('📤 Datos a enviar:', relacionData);
      console.log('📤 json_definicion:', relacionData.json_definicion);

      const response = await api.post(`/estudios/asociar`, relacionData);

      console.log('✅ Respuesta del servidor:', response.data);

      if (response.status === 201) {
        Swal.fire("Éxito", "Estudio asociado correctamente.", "success");
      } else if (response.status === 200) {
        Swal.fire("Éxito", "Relación reactivada correctamente.", "success");
      }

      await getEstudiosPorPatologia();
      setIdEstudio("");
      setFieldDefinition({}); // Resetear la configuración del campo

    } catch (error) {
      console.error("❌ Error al asociar estudio:", error);
      console.error("❌ Detalles del error:", error.response?.data);

      if (error.response?.status === 409 && error.response?.data?.code === "YA_ASOCIADO") {
        Swal.fire("Duplicado", "Este estudio ya está asociado a la patología seleccionada.", "error");
      } else {
        Swal.fire("Error", "No se pudo asociar el estudio. Intente nuevamente.", "error");
      }
    }
  };

  const getEstudiosPorPatologia = async () => {
    try {
      const response = await api.get(`/estudios-patologia/${IdPatologia}`);

      console.log('📋 Estudios obtenidos del servidor:', response.data);

      let data = response.data;

      // Si no es array, conviértelo en uno
      if (!Array.isArray(data)) {
        data = data ? [data] : [];
      }

      // Parsear json_definicion si viene como string
      const dataProcesada = data.map(estudio => {
        let jsonDef = estudio.json_definicion;
        
        // Si viene como string, parsearlo
        if (typeof jsonDef === 'string') {
          try {
            jsonDef = JSON.parse(jsonDef);
          } catch (err) {
            console.error('Error parseando json_definicion:', err);
            jsonDef = { tipo: "texto" };
          }
        }
        
        // Si es null o undefined, usar default
        if (!jsonDef) {
          jsonDef = { tipo: "texto" };
        }
        
        console.log(`Estudio ${estudio.Nombre} - json_definicion:`, jsonDef);
        
        return {
          ...estudio,
          json_definicion: jsonDef
        };
      });

      console.log('📋 Estudios procesados:', dataProcesada);
      setEstudiosXPatologia(dataProcesada);
    } catch (error) {
      console.error("Error al obtener estudios por patología:", error.message);
    }
  };

  const toggleEstadoEstudio = async (IdEstudio) => {
    try {
      await api.put(`/estudioPatToggleEstado`, {
        IdPatologia,
        IdEstudio,
      });

      await getEstudiosPorPatologia();

      Swal.fire("Éxito", "Estado actualizado.", "success");
    } catch (error) {
      if (error.response) {
        console.error("🛑 Error de respuesta del servidor:", error.response.data);
        console.error("🛑 Código de estado:", error.response.status);
      } else if (error.request) {
        console.error("🛑 No hubo respuesta del servidor:", error.request);
      } else {
        console.error("🛑 Error al configurar la solicitud:", error.message);
      }

      Swal.fire("Error", "No se pudo actualizar el estado.", "error");
    }
  };

  const actualizarEstudio = async (valores) => {
    try {
      // Validar que el tipo de dato esté configurado
      if (!valores.json_definicion || !valores.json_definicion.tipo) {
        Swal.fire("Configuración incompleta", "Debe configurar el tipo de dato para el estudio.", "warning");
        return;
      }

      // Si es de tipo selección, validar que tenga opciones
      if (
        [TIPOS_DATO.SELECCION, TIPOS_DATO.MULTIPLE].includes(valores.json_definicion.tipo) &&
        (!valores.json_definicion.configuracion?.opciones || valores.json_definicion.configuracion.opciones.length === 0)
      ) {
        Swal.fire("Configuración incompleta", "Para tipos de selección, debe agregar al menos una opción.", "warning");
        return;
      }

      // Actualizar el estudio (nombre y descripción)
      await api.patch(`/estudios/updateEstudio/${valores.IdEstudio}`, {
        Nombre: valores.Nombre,
        Descripcion: valores.Descripcion || "",
      });

      // Actualizar la relación estudio-patología con el nuevo json_definicion
      if (IdPatologia) {
        await api.put(`/estudios/actualizar-json-definicion`, {
          IdPatologia: parseInt(IdPatologia),
          IdEstudio: valores.IdEstudio,
          json_definicion: valores.json_definicion
        });
      }

      Swal.fire("Actualizado", "El estudio fue actualizado correctamente.", "success");
      setEditModalOpen(false);
      getEstudiosPorPatologia();
      getAllEstudios();
    } catch (error) {
      console.error("Error al actualizar estudio:", error);
      Swal.fire("Error", "No se pudo actualizar el estudio.", "error");
    }
  };

  const filtrarEstudios = () => {
    if (filtroEstado === "inactivas") {
      return estudiosXPatologia.filter(e => e.estadoRelacion === 0);
    }
    if (filtroEstado === "activas") {
      return estudiosXPatologia.filter(e => e.estadoRelacion === 1);
    }
    return estudiosXPatologia;
  };

  const columnas = [
    {
      name: "Nombre",
      selector: row => row.Nombre,
      width: "20%",
      sortable: true,
    },
    {
      name: "Descripción",
      selector: row => row.Descripcion || "Sin descripción",
      width: "30%",
      wrap: true,
    },
    {
      name: "Tipo de dato",
      selector: (row) => {
        const def = row.json_definicion || {};
        if (!def.tipo) return "Texto simple";
        
        // Si tiene opciones, mostrarlas
        if (def.tipo === "multiple" && def.configuracion?.opciones) {
          return `${def.tipo} (${def.configuracion.opciones.join(', ')})`;
        }
        if (def.tipo === "seleccion" && def.configuracion?.opciones) {
          return `${def.tipo} (${def.configuracion.opciones.join(', ')})`;
        }
        
        return def.tipo;
      },
      width: "30%",
      sortable: true,
      wrap: true,
    },
    {
      name: "Acción",
      cell: row => (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            className="button is-small is-info"
            type= "button"
            title="Editar"
            onClick={() => {
              setEstudioAEditar({
                IdEstudio: row.IdEstudio,
                Nombre: row.Nombre,
                Descripcion: row.Descripcion || "",
                json_definicion: row.json_definicion || { tipo: "", configuracion: {} },
              });
              setEditModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faPencil} />
          </button>
          <FontAwesomeIcon
            icon={row.estadoRelacion === 1 ? faSquarePlus : faSquareMinus}
            style={{ color: row.estadoRelacion === 1 ? "#37ff00" : "#ff0000" , cursor: "pointer" }}
            size="2xl"
            title={row.estadoRelacion === 1 ? "Desactivar" : "Activar"}
            onClick={() => {
              if (IdPatologia) toggleEstadoEstudio(row.IdEstudio);
              else Swal.fire("Advertencia", "Primero seleccione una patología.", "warning");
            }}
          />
        </div>
      ),
      width: "20%",
      center: true,
    },
  ];

  const paginationOptionsES = {
    rowsPerPageText: 'Filas por página',
    rangeSeparatorText: 'de',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Todos',
  };

  return (
    <div className="columns mt-5 is-centered">
      <div className="column is-half">
        <div
          className="notification is-primary"
          style={{
           
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
            fontFamily: "'Segoe UI', sans-serif",
            fontSize: "1.1rem",
            textAlign: "center",
          }}
        >
          <strong>Asignación de Estudios por Patología</strong>
        </div>
        <form onSubmit={crearRelacionEstudio}>
          <div className="field">
            <label className="label required">Seleccionar Patología</label>
            <div className="control">
              <select
                className="select is-fullwidth"
                value={IdPatologia}
                onChange={(e) => setIdPatologia(e.target.value)}
                required
              >
                <option value="">Selecciona</option>
                {allPatologias.map((p) => (
                  <option key={p.IdPatologia} value={p.IdPatologia}>
                    {p.Nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label className="label required">Seleccionar Estudio</label>
            <div className="control">
              <select
                className="select is-fullwidth"
                value={IdEstudio}
                onChange={(e) => setIdEstudio(e.target.value)}
                required
              >
                <option value="">Selecciona</option>
                {allEstudios.map((e) => (
                  <option key={e.IdEstudio} value={e.IdEstudio}>
                    {e.Nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Sección de configuración del campo parametrizable */}
          <div className="field">
            <label className="label">Configuración del campo (Opcional)</label>
            <p className="help">
              Configura el tipo de dato para este estudio en la patología seleccionada. 
              Si no se especifica, se usará texto simple.
            </p>
            <FieldDefinition 
              inicial={fieldDefinition}
              onChange={setFieldDefinition}
              showOptional={true}
            />
          </div>

          <div className="field">
            <button type="submit" className="button is-success">
              Guardar Relación
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
            {["todas", "activas", "inactivas"].map((estado) => (
              <button
                key={estado}
                onClick={() => setFiltroEstado(estado)}
                type="button"
                style={{
                  padding: "6px 16px",
                  border: "none",
                  borderRadius: "20px",
                  backgroundColor: filtroEstado === estado ? "#d1f5d3" : "#f0f2f5",
                  color: filtroEstado === estado ? "#075e54" : "#333",
                  fontWeight: filtroEstado === estado ? "bold" : "normal",
                  boxShadow:
                    filtroEstado === estado
                      ? "inset 0 0 0 1px #1ebea5"
                      : "inset 0 0 0 1px #ccc",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  fontSize: "0.9rem",
                }}
              >
                {estado.charAt(0).toUpperCase() + estado.slice(1)}
              </button>
            ))}
          </div>

          <div style={{ marginTop: '1rem' }}>
            <DataTable
              pagination
              paginationComponentOptions={paginationOptionsES}
              columns={columnas}
              data={filtrarEstudios()}
              customStyles={{
                table: {
                  style: {
                    width: '100%',
                  },
                },
              }}
            />
          </div>
        </form>

        {editModalOpen && (
          <EditModal
            title="Editar Estudio"
            initialValues={estudioAEditar}
            fields={[
              { name: "Nombre", label: "Nombre", type: "text", required: true },
              { name: "Descripcion", label: "Descripción", type: "textarea", required: false },
              {
                name: "json_definicion",
                label: "Configuración del campo",
                type: "custom",
                required: true,
                component: FieldDefinition,
                componentProps: {
                  allSymptoms: allEstudios,
                  sintomasPorPatologia: estudiosXPatologia,
                },
              },
            ]}
            onSubmit={actualizarEstudio}
            onClose={() => setEditModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AddEstudiosXPatologia;